/* @refresh reset */
import { useEffect, useMemo, useState } from 'react';
import { Button, Tree, TreeItem, TreeItemLayout, Spinner, Input, Subtitle2, Caption1 } from '@fluentui/react-components';
import { Edit16Regular, Building20Regular, Add16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { connector, Props } from './Structure.Functions';
import './Structure.Styles.css';
import SedesFormComponent from '../SedesForm/SedesForm.Component';
import SectorInlineForm from '../InlineForms/SectorInlineForm.Component';
import { Sector } from '../../../models/Entities/Turnero';

const StructureComponent = (
	{ 
		sedes, 
		sectores, 
		isLoading, 
		isUpdatingSector, 
		successUpdatingSector, 
		failOnUpdatingSector, 
		isLoadedSuccessfully,
		error, 
		GetSedes, 
		GetSectores, 
		UpdateSector, 
		onSectorSelected 
	}: Props) => {
	const { t } = useTranslation();
	const [isSedesFormOpen, setSedesFormOpen] = useState(false);
	const [activeBranchForSectorForm, setActiveBranchForSectorForm] = useState<number | null>(null);
	const [selectedSectorId, setSelectedSectorId] = useState<number | null>(null);
	const [editingSectorId, setEditingSectorId] = useState<number | null>(null);
	const [editName, setEditName] = useState('');
	const [editNumber, setEditNumber] = useState('');
	const [localSectores, setLocalSectores] = useState<Sector[]>(sectores || []);

	useEffect(() => {
		setLocalSectores(sectores || []);
	}, [sectores]);

	useEffect(() => {
		if (!isLoadedSuccessfully && !isLoading) {
			GetSedes();
			GetSectores();
		}
	}, [sedes, sectores]);

	useEffect(() => {
		if (successUpdatingSector && !isUpdatingSector) {
			setEditingSectorId(null);
			setEditName('');
			setEditNumber('');
		}
	}, [successUpdatingSector, isUpdatingSector]);

	const treeItems = useMemo(() => {
		return (sedes || []).map((sede, sedeIndex) => {
			const sedeKey = `sede-${sede.id ?? sedeIndex}`;
			const sedeTitle = sede.name ?? t('Offices.title');
			const sedeAddress = sede.direccion;
			const sedeId = sede.id;
			const sedeSectores = (localSectores || []).filter(
				(sector) => sector.branchId === sede.id || sector.branch?.id === sede.id
			);
			const showSectorForm = activeBranchForSectorForm === sedeId;

			return (
				<TreeItem itemType="branch" value={sedeKey} key={sedeKey}>
					<TreeItemLayout>
						<div className="structure-item">
							<div className="structure-item-title-row">
								<div className="structure-item-title">{sedeTitle}</div>
								<Button
									appearance="subtle"
									size="small"
									onClick={() => setActiveBranchForSectorForm(showSectorForm ? null : sedeId ?? null)}
									disabled={typeof sedeId !== 'number'}
								>
									{showSectorForm ? t('common.cancel', { defaultValue: 'Cancelar' }) : t('common.add', { defaultValue: 'Agregar sector' })}
								</Button>
							</div>
							{sedeAddress ? <div className="structure-item-subtitle">{sedeAddress}</div> : null}
						</div>
					</TreeItemLayout>
					<Tree>
						{sedeSectores.map((sector, sectorIndex) => {
							const sectorKey = `sector-${sector.id ?? `${sedeKey}-${sectorIndex}`}`;
							const sectorTitle = sector.name ?? t('sideMenu.consultorios');
							const sectorNumber = sector.number ?? (sector as any)?.codigo ?? (sector as any)?.code ?? (sector as any)?.numero;
							const isSelected = selectedSectorId === sector.id;
							const isEditing = editingSectorId === sector.id;

							return (
								<TreeItem itemType="leaf" value={sectorKey} key={sectorKey}>
									<TreeItemLayout onClick={() => {
										setSelectedSectorId(sector.id ?? null);
										onSectorSelected?.(sector.id ?? null);
									}}>
										{isEditing ? (
											<div className="structure-item structure-item-editing">
												<div className="structure-edit-fields">
													<Input
														size="small"
														value={editName}
														onChange={(e) => setEditName(e.currentTarget.value)}
														placeholder="Nombre"
													/>
													<Input
														size="small"
														value={editNumber}
														onChange={(e) => setEditNumber(e.currentTarget.value)}
														placeholder="Código"
													/>
												</div>
												<div className="structure-item-actions">
													<Button
														size="small"
														appearance="primary"
														onClick={(e) => {
															e.stopPropagation();
															if (sector.id !== undefined) {
																setLocalSectores((prev) => prev.map((s) => s.id === sector.id ? { ...s, name: editName.trim(), number: editNumber.trim() } : s));
																UpdateSector(sector.id, {
																	id: sector.id,
																	name: editName.trim(),
																	number: editNumber.trim(),
																	branchId: sector.branchId ?? sector.branch?.id ?? undefined,
																});
															}
														}}
														disabled={isUpdatingSector || !editName.trim()}
													>
														{isUpdatingSector ? <Spinner size="extra-tiny" /> : t('common.save', { defaultValue: 'Guardar' })}
													</Button>
													<Button
														size="small"
														onClick={(e) => {
															e.stopPropagation();
															setEditingSectorId(null);
															setEditName('');
															setEditNumber('');
														}}
													>
														{t('common.cancel', { defaultValue: 'Cancelar' })}
													</Button>
												</div>
												{failOnUpdatingSector && error?.ErrorMessage ? (
													<div className="structure-item-error">{error.ErrorMessage}</div>
												) : null}
											</div>
										) : (
											<div className={`structure-item${isSelected ? ' structure-item-selected' : ''}`}>
											<div className="structure-item-row">
												<div>
													<div className="structure-item-title">{sectorTitle}</div>
													{sectorNumber ? (
														<div className="structure-item-subtitle">{sectorNumber}</div>
													) : null}
												</div>
												<div className="structure-actions">
													<Button
														appearance="subtle"
														size="small"
														icon={<Edit16Regular />}
														onClick={(e) => {
															e.stopPropagation();
															setEditingSectorId(sector.id ?? null);
															setEditName(sectorTitle || '');
															setEditNumber(sectorNumber || '');
														}}
													/>
												</div>
											</div>
											</div>
										)}
									</TreeItemLayout>
								</TreeItem>
							);
						})}
						{showSectorForm && typeof sedeId === 'number' && (
							<TreeItem itemType="leaf" value={`sector-form-${sedeKey}`}>
								<TreeItemLayout>
									<div className="structure-inline-form">
										<SectorInlineForm
											branchId={sedeId}
											onCreated={() => {
												setActiveBranchForSectorForm(null);
												GetSectores();
												onSectorSelected?.(null);
											}}
											onCancel={() => setActiveBranchForSectorForm(null)}
										/>
									</div>
								</TreeItemLayout>
							</TreeItem>
						)}
					</Tree>
				</TreeItem>
			);
		});
	}, [sedes, localSectores, t, activeBranchForSectorForm, selectedSectorId, editingSectorId, editName, editNumber]);

	const showEmpty = !isLoading && treeItems.length === 0;

	return (
		<div className="structure-container">
			<div className="structure-header">
				<div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
						<Building20Regular style={{ color: '#0f6cbd', flexShrink: 0 }} />
						<Subtitle2>{t('Offices.title')}</Subtitle2>
					</div>
					<Caption1 style={{ color: 'var(--colorNeutralForeground3)' }}>
						{t('Offices.subtitle', { defaultValue: 'Sedes y sectores' })}
					</Caption1>
				</div>
				<Button appearance="primary" icon={<Add16Regular />} onClick={() => setSedesFormOpen(true)}>
					{t('common.create', { defaultValue: 'Crear Sede' })}
				</Button>
			</div>

			{isLoading ? (
				<div className="structure-loading">
					<Spinner label={t('common.loading', { defaultValue: 'Cargando...' })} />
				</div>
			) : showEmpty ? (
				<div className="structure-empty">{t('common.noData', { defaultValue: 'Sin datos' })}</div>
			) : (
				<Tree aria-label={t('Offices.title')}>
					{treeItems}
				</Tree>
			)}

			<SedesFormComponent isOpen={isSedesFormOpen} onClose={() => setSedesFormOpen(false)} />
		</div>
	);
};

export default connector(StructureComponent);